/**
 * Zod validation + (de)serialization for cinematic scene files.
 *
 * Import is the only untrusted entry point into the studio, so the schema is
 * written defensively: every field that isn't structurally required gets a
 * `.default()`. That doubles as the forward-compatibility story — a file written
 * by an older build loads with new fields filled in rather than being rejected,
 * and a file from a *newer* build warns but still parses, since zod drops keys
 * it doesn't know about.
 */
import { z } from 'zod'
import { makeId } from '@/features/flythrough'
import {
  CINEMATIC_SCENE_VERSION,
  DEFAULT_SCENE_DURATION_MS,
  type CinematicScene,
  type CinematicSceneFile,
} from './types'

const pos2 = z.tuple([z.number().finite(), z.number().finite()])

const pathWaypointSchema = z
  .object({
    id: z.string().optional(),
    pos: pos2,
    altitudeFt: z.number().finite().default(0),
    holdMs: z.number().finite().min(0).default(0),
  })
  .transform((w) => ({ ...w, id: w.id ?? makeId('pw') }))

const pathSchema = z.object({
  waypoints: z.array(pathWaypointSchema).min(2),
  headingMode: z.enum(['tangent', 'fixed']).default('tangent'),
  fixedHeadingDeg: z.number().finite().nullable().default(null),
  startOffsetMs: z.number().finite().min(0).default(0),
  durationMs: z.number().finite().min(100).default(20_000),
  playMode: z.enum(['once', 'loop', 'pingPong']).default('loop'),
  easing: z.enum(['linear', 'inOut', 'in', 'out']).default('inOut'),
  smooth: z.boolean().default(true),
})

const aircraftSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().default('Aircraft'),
    typeCode: z.string().min(1),
    position: pos2,
    headingDeg: z.number().finite().default(0),
    altitudeFt: z.number().finite().default(0),
    scale: z.number().finite().min(0.05).max(20).default(1),
    renderMode: z
      .enum(['solid', 'ghost_away', 'ghost_incoming', 'ghost_returning'])
      .default('solid'),
    path: pathSchema.nullable().default(null),
  })
  .transform((a) => ({ ...a, id: a.id ?? makeId('act') }))

const settingsSchema = z.object({
  mapStyleId: z
    .enum(['standard', 'satellite-streets', 'streets', 'light', 'dark', 'satellite'])
    .default('satellite-streets'),
  lightPreset: z.enum(['dawn', 'day', 'dusk', 'night']).default('day'),
  showBuildings: z.boolean().default(true),
  showRoofs: z.boolean().default(true),
  durationMs: z.number().finite().min(1000).default(DEFAULT_SCENE_DURATION_MS),
  loopScene: z.boolean().default(true),
})

const metaSchema = z.object({
  id: z.string().optional(),
  name: z.string().default('Untitled scene'),
  schemaVersion: z.number().int().positive().default(CINEMATIC_SCENE_VERSION),
  locationId: z.string().nullable().default(null),
  locationSlug: z.string().nullable().default(null),
  origin: pos2.nullable().default(null),
  createdAt: z.string().default(() => new Date().toISOString()),
  updatedAt: z.string().default(() => new Date().toISOString()),
})

// Camera tours are validated loosely here: the flythrough module owns their
// shape, and a malformed tour should not sink an otherwise-good scene.
const playlistSchema = z.object({
  id: z.string().default(() => makeId('fly')),
  name: z.string().default('Tour'),
  waypoints: z.array(z.any()).default([]),
  loop: z.boolean().default(false),
  hideUi: z.boolean().default(true),
  rampMs: z.number().finite().min(0).default(800),
  speed: z.number().finite().min(0.05).max(8).default(1),
  restoreCameraOnStop: z.boolean().default(true),
})

const sceneSchema = z.object({
  meta: metaSchema,
  settings: settingsSchema,
  aircraft: z.array(aircraftSchema).default([]),
  playlists: z.array(playlistSchema).default([]),
})

const fileSchema = z.object({
  kind: z.literal('planmax.cinematic.scene'),
  version: z.number().int().positive(),
  scene: sceneSchema,
})

export interface ParseSceneResult {
  ok: boolean
  scene?: CinematicScene
  error?: string
  /** Set when the file came from a newer build than this one understands. */
  warning?: string
}

/**
 * Parse a scene file. Never throws — the caller surfaces `error` in a toast.
 *
 * Always assigns a fresh scene id so importing twice yields two scenes rather
 * than silently overwriting one.
 */
export function parseSceneFile(raw: unknown): ParseSceneResult {
  const result = fileSchema.safeParse(raw)
  if (!result.success) {
    const first = result.error.issues[0]
    const where = first?.path.length ? ` at ${first.path.join('.')}` : ''
    return { ok: false, error: `${first?.message ?? 'Invalid scene file'}${where}` }
  }

  const warning =
    result.data.version > CINEMATIC_SCENE_VERSION
      ? `This scene was saved by a newer version (v${result.data.version}). ` +
        `Anything this build doesn't understand was dropped.`
      : undefined

  const parsed = result.data.scene
  const scene: CinematicScene = {
    ...parsed,
    meta: {
      ...parsed.meta,
      id: makeId('scene'),
      schemaVersion: CINEMATIC_SCENE_VERSION,
      updatedAt: new Date().toISOString(),
    },
  } as CinematicScene

  return { ok: true, scene, warning }
}

export function serializeScene(scene: CinematicScene): CinematicSceneFile {
  return { kind: 'planmax.cinematic.scene', version: CINEMATIC_SCENE_VERSION, scene }
}

/** Round persisted floats — feet to 0.1, lng/lat to 6dp — to keep JSON small. */
export function compactScene(scene: CinematicScene): CinematicScene {
  const r1 = (n: number) => Math.round(n * 10) / 10
  return {
    ...scene,
    aircraft: scene.aircraft.map((a) => ({
      ...a,
      position: [r1(a.position[0]), r1(a.position[1])],
      headingDeg: r1(a.headingDeg),
      altitudeFt: r1(a.altitudeFt),
      path: a.path
        ? {
            ...a.path,
            waypoints: a.path.waypoints.map((w) => ({
              ...w,
              pos: [r1(w.pos[0]), r1(w.pos[1])] as [number, number],
              altitudeFt: r1(w.altitudeFt),
            })),
          }
        : null,
    })),
  }
}
